<template>
  <div class="db-health-tab">
    <!-- Bộ điều khiển: range, node filter, refresh -->
    <div class="d-flex align-items-center mb-3">
      <span class="text-muted mr-2">Khoảng:</span>
      <select v-model="range" class="form-control form-control-sm mr-3" style="width:auto" @change="loadAll">
        <option value="1h">1 giờ qua</option>
        <option value="6h">6 giờ qua</option>
        <option value="24h">24 giờ qua</option>
        <option value="7d">7 ngày qua</option>
      </select>
      <span class="text-muted mr-2">Node:</span>
      <select v-model="nodeFilter" class="form-control form-control-sm mr-3" style="width:auto" @change="loadAll">
        <option :value="null">Tất cả</option>
        <option v-for="n in nodeOptions" :key="n" :value="n">{{ n }}</option>
      </select>
      <button class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="loadAll">
        <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i> Tải lại
      </button>
      <span v-if="lastUpdatedStr" class="text-muted small ml-3"><i class="fa fa-clock-o"></i> {{ lastUpdatedStr
        }}</span>
      <button v-if="isOwner" class="btn btn-sm btn-outline-danger is-border-1 ml-auto" :disabled="clearing"
        @click="clearData" title="Xoá snapshot để theo dõi lại từ đầu sau khi fix">
        <i :class="clearing ? 'fa fa-spinner fa-spin' : 'fa fa-eraser'"></i> Xoá dữ liệu theo dõi
      </button>
    </div>

    <!-- Banner thông báo nếu chưa cấu hình credentials (chỉ owner thấy + có quick link) -->
    <div v-if="isOwner && !credConfigured" class="alert alert-warning mb-3">
      <i class="fa fa-key"></i> <b>Monitor credentials chưa cấu hình</b>
      — DB Health Monitor và <code>$indexStats</code> chưa hoạt động.
      Vào <router-link to="/etc">Tham số hệ thống</router-link> → nhóm <b>"DB Health Monitor"</b> để đặt user/password.
    </div>

    <!-- Active alerts banner -->
    <div v-if="activeAlerts.length" class="alert alert-warning mb-3">
      <b><i class="fa fa-exclamation-triangle"></i> Có {{ activeAlerts.length }} cảnh báo chưa xử lý</b>
      <ul class="mb-0 mt-1">
        <li v-for="a in activeAlerts.slice(0, 5)" :key="a._id">
          <span class="badge" :class="a.alert.severity === 'critical' ? 'badge-danger' : 'badge-warning'">{{
            a.alert.severity }}</span>
          <code>{{ a.alert.alertType }}</code> @ <code>{{ a.node }}</code> — {{ alertSummary(a) }}
          <button class="btn btn-sm btn-link" @click="resolve(a)">Đánh dấu xong</button>
        </li>
      </ul>
    </div>

    <!-- Summary cards cho từng node -->
    <div class="row mb-3">
      <div class="col-md-6" v-for="s in sortedNodes" :key="s.node">
        <div class="card">
          <div class="card-header">
            <b><i class="fa fa-database"></i> {{ s.node }}</b>
            <small class="text-muted ml-2">{{ s.host }}</small>
            <small class="text-muted float-right">{{ tsAgo(s.createTime) }}</small>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-3">
                <div class="text-muted small">Query/s</div>
                <div class="h5">{{ s.snapshot.opcountersDelta.query }}</div>
              </div>
              <div class="col-3">
                <div class="text-muted small">Insert/s</div>
                <div class="h5">{{ s.snapshot.opcountersDelta.insert }}</div>
              </div>
              <div class="col-3">
                <div class="text-muted small">Update/s</div>
                <div class="h5">{{ s.snapshot.opcountersDelta.update }}</div>
              </div>
              <div class="col-3">
                <div class="text-muted small">Command/s</div>
                <div class="h5">{{ s.snapshot.opcountersDelta.command }}</div>
              </div>
            </div>
            <hr class="my-2" />
            <div class="row">
              <div class="col-4">
                <div class="text-muted small">Queue R/W</div>
                <div class="h6" :class="queueClass(s.snapshot.globalLockQueue)">{{ s.snapshot.globalLockQueue.readers }}
                  / {{ s.snapshot.globalLockQueue.writers }}</div>
              </div>
              <div class="col-4">
                <div class="text-muted small">Connections</div>
                <div class="h6">{{ s.snapshot.connections.current }} / {{ s.snapshot.connections.current +
                  s.snapshot.connections.available }}</div>
              </div>
              <div class="col-4" v-if="s.snapshot.replicationLagMs > 0">
                <div class="text-muted small">Repl lag</div>
                <div class="h6" :class="s.snapshot.replicationLagMs > 5000 ? 'text-danger' : 'text-success'">{{
                  fmtMs(s.snapshot.replicationLagMs) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Throughput chart -->
    <div class="card mb-3">
      <div class="card-header"><b>Throughput {{ rangeLabel }}</b></div>
      <div class="card-body">
        <apexchart v-if="chartReady" type="line" height="280" :options="chartOptions" :series="chartSeries" />
        <div v-else class="text-center text-muted py-4"><i class="fa fa-spinner fa-spin"></i> Đang tải...</div>
      </div>
    </div>

    <!-- Slow ops table -->
    <div class="card mb-3">
      <div class="card-header">
        <b>Slow operations ({{ slowOps.length }})</b> — các thao tác DB chạy &gt;1s, gộp theo (namespace + plan + client
        IP)
        <button class="btn btn-link btn-sm p-0 ml-2" @click="showSlowHelp = !showSlowHelp">
          <i class="fa fa-question-circle"></i> {{ showSlowHelp ? 'Ẩn' : 'Giải thích' }}
        </button>
      </div>
      <div v-if="showSlowHelp" class="card-body border-bottom small text-muted py-2">
        <div><b>NS</b>: collection bị truy vấn (vd <code>phonenet.customers</code>).</div>
        <div><b>Plan</b>: cách MongoDB chạy. <span class="badge badge-danger">COLLSCAN</span> = quét TOÀN BỘ collection
          (thiếu index → CHẬM, cần fix). <span class="badge badge-secondary">IXSCAN</span> = dùng index (tốt).
          <code>createIndexes</code> = đang build index (tạm thời).
        </div>
        <div><b>Max secs</b>: thời gian lâu nhất 1 lần chạy (giây). <b>Yields</b>: số lần nhường CPU — càng cao = quét
          càng nhiều doc.</div>
        <div><b>Lần</b>: số lần op này xuất hiện trong khoảng. <b>Client IP</b>: server gọi (IP nội bộ = service
          Callio).</div>
        <div class="text-info mt-1"><i class="fa fa-lightbulb-o"></i> Ưu tiên xử lý: COLLSCAN + Max secs cao + Lần
          nhiều. Bấm 1 dòng để xem câu lệnh đầy đủ.</div>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0">
            <thead class="thead-light">
              <tr>
                <th @click="setSort('ns')" style="cursor:pointer">NS <i :class="sortIcon('ns')"></i></th>
                <th @click="setSort('plan')" style="cursor:pointer">Plan <i :class="sortIcon('plan')"></i></th>
                <th @click="setSort('maxSecs')" class="text-right" style="cursor:pointer">Max secs <i
                    :class="sortIcon('maxSecs')"></i></th>
                <th @click="setSort('maxYields')" class="text-right" style="cursor:pointer">Yields <i
                    :class="sortIcon('maxYields')"></i></th>
                <th @click="setSort('count')" class="text-right" style="cursor:pointer">Lần <i
                    :class="sortIcon('count')"></i></th>
                <th>Client IP</th>
                <th>Nodes</th>
                <th @click="setSort('lastSeen')" style="cursor:pointer">Lần cuối <i :class="sortIcon('lastSeen')"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in sortedSlowOps" :key="rowKey(o)" @click="viewOp(o)" style="cursor:pointer">
                <td><code>{{ o._id.ns }}</code></td>
                <td><span class="badge" :class="o._id.plan === 'COLLSCAN' ? 'badge-danger' : 'badge-secondary'">{{
                  o._id.plan || '—' }}</span></td>
                <td class="text-right">{{ o.maxSecs }}s</td>
                <td class="text-right">{{ o.maxYields }}</td>
                <td class="text-right">{{ o.count }}</td>
                <td><small>{{ o._id.clientIp }}</small></td>
                <td><small>{{ (o.nodes || []).join(', ') }}</small></td>
                <td><small>{{ tsAgo(o.lastSeen) }}</small></td>
              </tr>
              <tr v-if="!slowOps.length">
                <td colspan="8" class="text-center text-muted py-3">Không có slow op trong khoảng này 🎉</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Index stats lookup -->
    <div class="card">
      <div class="card-header">
        <b>$indexStats LIVE</b>
        <select v-model="indexCollection" class="form-control form-control-sm ml-2 d-inline-block" style="width:auto"
          @change="loadIndexStats">
          <option v-for="c in indexCollections" :key="c" :value="c">{{ c }}</option>
        </select>
        <small class="text-muted ml-2">Top index theo ops từ lúc mongod start</small>
      </div>
      <div class="card-body p-0">
        <div v-if="indexStatsError" class="alert alert-warning m-3 mb-0">
          <i class="fa fa-exclamation-triangle"></i> {{ indexStatsError }}
        </div>
        <div class="table-responsive" style="max-height:400px;overflow:auto">
          <table class="table table-sm mb-0">
            <thead class="thead-light">
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th class="text-right">Ops</th>
                <th>Since</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="idx in indexStats" :key="idx.name" :class="idx.ops === 0 ? 'text-muted' : ''">
                <td><code>{{ idx.name }}</code></td>
                <td><code>{{ JSON.stringify(idx.key) }}</code></td>
                <td class="text-right">{{ fmtNum(idx.ops) }}</td>
                <td><small>{{ idx.since ? new Date(idx.since).toLocaleDateString('vi-VN') : '—' }}</small></td>
              </tr>
              <tr v-if="!indexStats.length && !indexStatsError">
                <td colspan="4" class="text-center text-muted py-3">Đang tải...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal chi tiết slow op -->
    <div v-if="selectedOp" class="modal-backdrop-custom" @click.self="selectedOp = null">
      <div class="modal-content-custom">
        <div class="d-flex align-items-center mb-2">
          <h5 class="mb-0">Chi tiết slow op</h5>
          <button class="btn btn-sm btn-outline-primary is-border-1 ml-auto" @click="copyOp">
            <i :class="copied ? 'fa fa-check text-success' : 'fa fa-copy'"></i> {{ copied ? 'Đã copy' : 'Copy' }}
          </button>
        </div>
        <pre>{{ formatOp(selectedOp) }}</pre>
        <button class="btn btn-secondary" @click="selectedOp = null">Đóng</button>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'DbHealthTab',
  components: { 'apexchart': VueApexCharts },
  data() {
    return {
      range: '6h',
      nodeFilter: null,
      loading: false,
      lastUpdatedStr: '',
      summary: { nodes: [], activeAlerts: 0 },
      snapshots: [],
      slowOps: [],
      activeAlerts: [],
      indexStats: [],
      indexStatsError: '',
      indexCollections: ['customers', 'customerlogs', 'calls', 'opportunities', 'tickets', 'tasks', 'productorders'],
      indexCollection: 'customers',
      selectedOp: null,
      credConfigured: false,
      clearing: false,
      showSlowHelp: false,
      sortKey: 'maxSecs',
      sortDir: 'desc',
      copied: false,
    };
  },
  computed: {
    isOwner() {
      const role = this.$store && this.$store.state.user && this.$store.state.user.role;
      return role === 'owner';
    },
    // Sort node cố định (primary trước) để 2 ô không nhảy chỗ mỗi lần refresh.
    sortedNodes() {
      return (this.summary.nodes || []).slice().sort((a, b) => String(a.node).localeCompare(String(b.node)));
    },
    sortedSlowOps() {
      const k = this.sortKey, dir = this.sortDir === 'asc' ? 1 : -1;
      const val = (o) => {
        if (k === 'ns') return o._id.ns || '';
        if (k === 'plan') return o._id.plan || '';
        return o[k] || 0;
      };
      return (this.slowOps || []).slice().sort((a, b) => {
        const va = val(a), vb = val(b);
        if (typeof va === 'string') return va.localeCompare(vb) * dir;
        return (va - vb) * dir;
      });
    },
    rangeLabel() {
      return { '1h': '1 giờ qua', '6h': '6 giờ qua', '24h': '24 giờ qua', '7d': '7 ngày qua' }[this.range] || this.range;
    },
    nodeOptions() {
      const set = new Set(this.summary.nodes.map(n => n.node));
      return [...set];
    },
    chartReady() { return this.snapshots.length > 0; },
    chartSeries() {
      const byNode = {};
      for (const s of this.snapshots) {
        if (!byNode[s.node]) byNode[s.node] = { query: [], write: [] };
        const t = s.createTime;
        const d = s.snapshot || {};
        var oc = d.opcountersDelta || {};
        byNode[s.node].query.push([t, oc.query || 0]);
        byNode[s.node].write.push([t, (oc.insert || 0) + (oc.update || 0) + (oc.delete || 0)]);
      }
      const series = [];
      for (const node of Object.keys(byNode)) {
        series.push({ name: `${node} query/s`, data: byNode[node].query });
        series.push({ name: `${node} write/s`, data: byNode[node].write });
      }
      return series;
    },
    chartOptions() {
      return {
        chart: { id: 'db-throughput', toolbar: { show: false }, animations: { enabled: false } },
        // datetimeUTC:false → hiển thị giờ trình duyệt (UTC+7 cho VN) thay vì UTC. Data lưu epoch ms.
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: { labels: { formatter: v => Math.round(v) } },
        stroke: { width: 1.5, curve: 'straight' },
        legend: { position: 'top' },
        tooltip: { x: { format: 'HH:mm:ss dd/MM' }, shared: true },
      };
    },
  },
  mounted() {
    this.loadAll();
    if (this.isOwner) this.loadCredStatus();
    this._timer = setInterval(() => this.loadAll(true), 30000);
  },
  beforeDestroy() {
    if (this._timer) clearInterval(this._timer);
  },
  methods: {
    fmtMs(v) { return Math.round(v) + 'ms'; },
    fmtNum(v) {
      if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
      if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
      return String(v);
    },
    tsAgo(t) {
      if (!t) return '—';
      const d = Date.now() - t;
      if (d < 60000) return Math.round(d / 1000) + 's trước';
      if (d < 3600000) return Math.round(d / 60000) + 'p trước';
      if (d < 86400000) return Math.round(d / 3600000) + 'h trước';
      return Math.round(d / 86400000) + 'd trước';
    },
    queueClass(q) {
      if (!q) return '';
      if (q.readers > 10 || q.writers > 5) return 'text-danger';
      if (q.readers > 5 || q.writers > 2) return 'text-warning';
      return 'text-success';
    },
    alertSummary(a) {
      const d = (a.alert && a.alert.detail) || {};
      if (a.alert.alertType === 'collscan') return `${d.ns} ${d.secs}s (yields ${d.yields})`;
      if (a.alert.alertType === 'replication-lag') return `lag ${d.lagMs}ms`;
      if (a.alert.alertType === 'queue-readers') return `readers=${d.readers}`;
      if (a.alert.alertType === 'queue-writers') return `writers=${d.writers}`;
      if (a.alert.alertType === 'slow-op') return `${d.ns} ${d.secs}s`;
      if (a.alert.alertType === 'connection-pool') return `${d.connCurrent}/${d.connCurrent + d.connAvailable}`;
      return '';
    },
    rowKey(o) { return `${o._id.ns}|${o._id.plan}|${o._id.clientIp}`; },
    setSort(k) {
      if (this.sortKey === k) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; }
      else { this.sortKey = k; this.sortDir = (k === 'ns' || k === 'plan') ? 'asc' : 'desc'; }
    },
    sortIcon(k) {
      if (this.sortKey !== k) return 'fa fa-sort text-muted';
      return this.sortDir === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down';
    },
    viewOp(o) { this.selectedOp = o; this.copied = false; },
    copyOp() {
      const text = this.formatOp(this.selectedOp);
      const done = () => { this.copied = true; setTimeout(() => { this.copied = false; }, 2000); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
      } else {
        this.fallbackCopy(text, done);
      }
    },
    fallbackCopy(text, done) {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    },
    formatOp(o) {
      let cmd = o.sampleCommand;
      try { cmd = JSON.stringify(JSON.parse(o.sampleCommand), null, 2); } catch (_) {
        // nếu không parse được (vd command quá cũ, chỉ có string), giữ nguyên format gốc để dễ đọc hơn.
      }
      return `Namespace: ${o._id.ns}\nPlan: ${o._id.plan}\nMax secs: ${o.maxSecs}\nMax yields: ${o.maxYields}\nCount: ${o.count}\nFirst seen: ${new Date(o.firstSeen).toLocaleString('vi-VN')}\nLast seen: ${new Date(o.lastSeen).toLocaleString('vi-VN')}\nNodes: ${(o.nodes || []).join(', ')}\nClient: ${o._id.clientIp}\n\nCommand:\n${cmd}`;
    },
    async loadAll(silent = false) {
      if (!silent) this.loading = true;
      try {
        const params = { range: this.range };
        if (this.nodeFilter) params.node = this.nodeFilter;
        const [summary, snapshots, slowOps, alerts] = await Promise.all([
          this.$service.get('/db-health/summary'),
          this.$service.get('/db-health/snapshots', { params }),
          this.$service.get('/db-health/slow-ops', { params }),
          this.$service.get('/db-health/alerts', { params: { range: this.range, active: 1 } }),
        ]);
        this.summary = summary.data || { nodes: [] };
        this.snapshots = (snapshots.data && snapshots.data.docs) || [];
        this.slowOps = (slowOps.data && slowOps.data.ops) || [];
        this.activeAlerts = (alerts.data && alerts.data.alerts) || [];
        if (!this.indexStats.length) await this.loadIndexStats();
        this.lastUpdatedStr = new Date().toLocaleTimeString('vi-VN');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Lỗi loadAll:', e);
      } finally {
        this.loading = false;
      }
    },
    async loadIndexStats() {
      this.indexStatsError = '';
      try {
        const r = await this.$service.get('/db-health/index-stats', { params: { collection: this.indexCollection } });
        this.indexStats = (r.data && r.data.indexes) || [];
      } catch (e) {
        this.indexStats = [];
        const msg = e && e.response && e.response.data && (e.response.data.hint || e.response.data.message);
        this.indexStatsError = msg || 'Lỗi tải $indexStats';
      }
    },
    async clearData() {
      if (!confirm('Xoá toàn bộ snapshot theo dõi (giữ lịch sử alert) để bắt đầu theo dõi lại từ đầu sau khi fix?\nWorker sẽ ghi snapshot mới ở tick kế tiếp (~30s).')) return;
      this.clearing = true;
      try {
        const r = await this.$service.delete('/db-health/data', { params: { scope: 'snapshot' } });
        const n = (r.data && r.data.deleted) || 0;
        this.snapshots = [];
        this.slowOps = [];
        await this.loadAll();
        alert(`Đã xoá ${n} snapshot. Theo dõi lại từ bây giờ.`);
      } catch (e) {
        alert('Xoá thất bại: ' + (e && e.response && e.response.data && e.response.data.message || e.message));
      } finally {
        this.clearing = false;
      }
    },
    async loadCredStatus() {
      try {
        const r = await this.$service.get('/db-health/credentials');
        this.credConfigured = !!(r.data && r.data.configured);
      } catch (e) {
        this.credConfigured = false;
      }
    },
    async resolve(alert) {
      try {
        await this.$service.post(`/db-health/alerts/${alert._id}/resolve`);
        this.activeAlerts = this.activeAlerts.filter(a => a._id !== alert._id);
      } catch (e) { /* ignore */ }
    },
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content-custom {
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow: auto;
}

.modal-content-custom pre {
  background: #f4f4f4;
  padding: 10px;
  border-radius: 4px;
  max-height: 50vh;
  overflow: auto;
  font-size: 12px;
}

.badge {
  font-size: 11px;
}

code {
  color: #d63384;
  background: #f8f9fa;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 12px;
}
</style>
